// Ties the session's live navigation state (ExperienceSession.state.navigation)
// to the compass/map cue and the share-location gate. Lives entirely in
// src/glasses/nav/ so it never collides with the beats or typed-reply work
// happening in the same session on card/, ChipRow.tsx, hudView, glasses.css.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { BrowserGeolocation, toastDurationMs } from '@aeonia/gaia-client';
import type { ExperienceSession } from '@aeonia/gaia-client';
import { DossierCard } from '../card/DossierCard';
import CompassView from '../map/CompassView';
import SecretMapView from '../map/SecretMapView';
import { bearingDeg, bearingFromFixes, distanceM, relativeAngleDeg } from '../map/geo';
import type { LatLng } from '../map/geo';
import { useHeading } from '../map/useHeading';
import { NavigationLocationController } from './NavigationLocationController';
import type { LocationWatchPort, NavigationFix } from './NavigationLocationController';
import { INITIAL_THROTTLE_STATE, shouldSendPosition } from './throttle';
import { parseSimulatedFix, parseSimulatedHeading } from './params';
import ShareLocationButton from './ShareLocationButton';
import '../map/map.css';
import './nav.css';

/** The confirmation shown once location starts (tap, or the automatic start when permission was already granted), before the compass/map cue takes the card slot. */
const LOCATION_SHARED_TEXT = 'On your way.';

/** The generic confirmation is for the first leg only; later legs go straight to the line or the compass. */
let locationConfirmationShown = false;
export function resetLocationConfirmationForTests(): void {
  locationConfirmationShown = false;
}

/** Wraps the shared BrowserGeolocation adapter as a LocationWatchPort. */
function browserGeolocationPort(): LocationWatchPort {
  const geo = new BrowserGeolocation();
  let watchId: number | null = null;
  let generation = 0;
  return {
    start(onFix, onUnavailable) {
      const request = ++generation;
      void geo.requestPermission().then((perm) => {
        if (request !== generation) return;
        if (perm !== 'granted') { onUnavailable?.(); return; }
        const startedWatch = geo.watchPosition((pos) => {
          if (request === generation) onFix({ lat: pos.lat, lng: pos.lng, accuracyM: pos.accuracy });
        }, () => { if (request === generation) onUnavailable?.(); });
        if (request === generation) watchId = startedWatch;
        else geo.clearWatch(startedWatch);
      });
    },
    stop() {
      generation++;
      if (watchId !== null) geo.clearWatch(watchId);
      watchId = null;
    },
  };
}

/**
 * A dev-only port that reports a fixed simulated fix immediately: for
 * "?fake=1&nav=1&lat=&lng=" demos with no server and no real GPS (Meta's
 * simulator has none).
 */
function simulatedLocationPort(fix: SimulatedFix): LocationWatchPort {
  return {
    start(onFix) {
      onFix({ lat: fix.lat, lng: fix.lng, accuracyM: 5 });
    },
    stop() {
      /* nothing to tear down */
    },
  };
}

type SimulatedFix = ReturnType<typeof parseSimulatedFix> extends infer T ? NonNullable<T> : never;

export interface NavigationOverlayProps {
  session: ExperienceSession;
  /** Phone orientation only; glasses retain travel-derived heading. */
  deviceHeading?: boolean;
  target: LatLng;
  label: string;
  arrivalRadiusM: number;
  show: 'compass' | 'map';
  /** The page's query string, for the "?fake=1" simulated fix override. */
  search: string;
  revealKey: number;
  /** Throckmorton's current line, shown above the fallback button so the player knows where to go. */
  lineCard?: ReactNode;
  /** The same line as text: when a leg starts, it is held on screen this long (instead of "On your way.") before the compass takes over. */
  lineText?: string;
  /**
   * The outcome of the one-shot permission ask made on the start tap
   * (GlassesApp.tsx). `null` while that ask is still outstanding. Granted
   * starts location automatically with no button; denied/unavailable falls
   * back to the "On my way" button.
   */
  locationPermission: 'granted' | 'denied' | 'unavailable' | null;
}

/**
 * Starts location automatically when permission was already granted, otherwise renders the "On my way" fallback until the player taps it; then the
 * compass or map cue driven by live (or simulated) fixes. Only the latest
 * fix is ever kept in memory (R-SAFE-01: never store the position trail).
 */
export default function NavigationOverlay(props: NavigationOverlayProps) {
  const { session, target, label, arrivalRadiusM, show, search, revealKey, lineCard, lineText, locationPermission, deviceHeading = false } = props;

  const simulatedDemo = import.meta.env.DEV && new URLSearchParams(search).get('demoLocation') === '1';
  const [fix, setFix] = useState<NavigationFix | null>(null);
  const prevFix = useRef<LatLng | null>(null);
  const travelBearing = useRef<number | null>(null);
  const throttleState = useRef(INITIAL_THROTTLE_STATE);

  const controllerRef = useRef<NavigationLocationController | null>(null);
  if (controllerRef.current === null) {
    const simulated = parseSimulatedFix(search);
    const port = simulated ? simulatedLocationPort(simulated) : browserGeolocationPort();
    controllerRef.current = new NavigationLocationController(port);
  }
  const controller = controllerRef.current;
  const [shared, setShared] = useState(controller.isShared);
  const [locationFailed, setLocationFailed] = useState(false);
  // Meta's toast timing (beats.ts toastDurationMs): the "On your way." confirmation
  // status card shows for this long, then clears itself so the compass/map
  // cue takes the slot.
  const [showConfirmation, setShowConfirmation] = useState(false);
  // Decided when the leg starts: only the first leg without a line uses the generic text.
  const [genericConfirmation, setGenericConfirmation] = useState(false);
  const confirmationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (confirmationTimer.current) clearTimeout(confirmationTimer.current);
    };
  }, []);

  const onFix = useCallback(
    (next: NavigationFix) => {
      if (prevFix.current) {
        const bearing = bearingFromFixes(prevFix.current, next);
        if (bearing !== null) travelBearing.current = bearing;
      }
      prevFix.current = { lat: next.lat, lng: next.lng };
      setFix(next);

      const insideArrival = distanceM(next, target) <= arrivalRadiusM;
      const decision = shouldSendPosition(throttleState.current, next, Date.now(), insideArrival);
      if (decision.send && session.sendPosition(next.lat, next.lng, next.accuracyM)) {
        throttleState.current = decision.nextState;
      }
    },
    [session, target, arrivalRadiusM]
  );

  const onUnavailable = useCallback(() => {
    setShared(false); setLocationFailed(true); setShowConfirmation(false);
    if (confirmationTimer.current) clearTimeout(confirmationTimer.current);
    confirmationTimer.current = null;
  }, []);

  const onShare = useCallback(() => {
    setLocationFailed(false);
    setShared(true);
    const showLine = Boolean(lineCard && lineText);
    if (!showLine && locationConfirmationShown) {
      controller.share(onFix, onUnavailable);
      return;
    }
    if (!showLine) locationConfirmationShown = true;
    setGenericConfirmation(!showLine);
    setShowConfirmation(true);
    if (confirmationTimer.current) clearTimeout(confirmationTimer.current);
    confirmationTimer.current = setTimeout(() => {
      confirmationTimer.current = null;
      setShowConfirmation(false);
    }, toastDurationMs(lineCard && lineText ? lineText : LOCATION_SHARED_TEXT));
    controller.share(onFix, onUnavailable);
  }, [controller, onFix, onUnavailable, lineCard, lineText]);

  // Watch for as long as this overlay is mounted once the player has shared,
  // and stop when it unmounts (navigation cleared, or the session ended).
  // React's development StrictMode mounts, unmounts and mounts again; the
  // second mount must restart the watch the simulated unmount stopped.
  const latestOnFix = useRef(onFix);
  latestOnFix.current = onFix;
  const latestOnUnavailable = useRef(onUnavailable);
  latestOnUnavailable.current = onUnavailable;
  useEffect(() => {
    if (controller.isShared) {
      controller.share((next) => latestOnFix.current(next), () => latestOnUnavailable.current());
    }
    return () => controller.stop();
  }, [controller]);

  // Permission was already granted on the start tap: start location the
  // moment the walk arrives, with no button (Jason, 2026-09-29). Guarded by
  // `shared` so this only ever fires once.
  useEffect(() => {
    if (!shared && !locationFailed && locationPermission === 'granted') onShare();
  }, [shared, locationFailed, locationPermission, onShare]);

  const heading = useHeading(parseSimulatedHeading(search), travelBearing.current, null, deviceHeading);

  if (!shared || !fix) {
    // Permission was denied, unavailable, or the one-shot ask is still
    // outstanding: no button until we know it was not granted, so the
    // display never flashes the fallback button then yanks it away.
    if (!locationFailed && (locationPermission === 'granted' || locationPermission === null)) {
      return <>{lineCard}</>;
    }
    return (
      <>
        {lineCard}
        {locationFailed && <p role="status">Location unavailable. Try again or keep talking.</p>}
        <ShareLocationButton onShare={onShare} />
      </>
    );
  }

  if (showConfirmation) {
    // Throckmorton's direction line says where to go; the generic confirmation
    // only stands in when a leg arrives without one.
    if (lineCard) return <>{lineCard}</>;
    if (genericConfirmation) return <DossierCard variant="status" body={LOCATION_SHARED_TEXT} />;
  }

  const bearing = bearingDeg(fix, target);
  const relative = heading.deg !== null ? relativeAngleDeg(bearing, heading.deg) : null;
  const dist = distanceM(fix, target);

  if (show === 'map') {
    return (
      <SecretMapView
        player={fix}
        target={target}
        label={label}
        upHeadingDeg={heading.deg ?? 0}
        headingKnown={heading.deg !== null}
        marks={[]}
        basemap={null}
        units="m"
        arrivalRadiusM={arrivalRadiusM}
        revealKey={revealKey}
      />
    );
  }

  return (
    <>
    <CompassView
      bearingDeg={bearing}
      relativeDeg={relative}
      distanceM={dist}
      units="m"
      label={label}
      arrivalRadiusM={arrivalRadiusM}
      revealKey={revealKey}
    />
    {simulatedDemo && <div className="navigation-demo-controls" aria-label="Simulated movement controls" style={{position: 'fixed', bottom: 8, left: 8, zIndex: 10, color: '#ffc14d', fontSize: '.875rem'}}>
      <span>Simulated movement</span>{' '}
      <button onClick={() => onFix({lat: target.lat + 0.01, lng: target.lng, accuracyM: 5})}>Outside</button>{' '}
      <button onClick={() => onFix({lat: target.lat, lng: target.lng, accuracyM: 100})}>Poor accuracy</button>{' '}
      <button onClick={() => onFix({lat: target.lat, lng: target.lng, accuracyM: 5})}>At destination</button>
    </div>}
    </>
  );
}
